import React from 'react';
import { Mic, MicOff, X, Check, Loader2 } from 'lucide-react';

interface VoiceRecordingOverlayProps {
  isOpen: boolean;
  isListening: boolean;
  transcript: string;
  interimTranscript: string;
  error: string | null;
  isProcessing: boolean;
  onClose: () => void;
  onToggleListening: () => void;
  onValidate: () => void;
  onCancel: () => void;
}

export const VoiceRecordingOverlay: React.FC<VoiceRecordingOverlayProps> = ({
  isOpen,
  isListening,
  transcript,
  interimTranscript,
  error,
  isProcessing,
  onClose,
  onToggleListening,
  onValidate,
  onCancel,
}) => {
  if (!isOpen) return null;

  const hasTranscript = transcript.trim().length > 0;

  return (
    <div className="fixed inset-0 z-[100] bg-gradient-to-b from-gray-900 to-gray-800 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 safe-area-top">
        <h2 className="text-white font-semibold text-lg">Saisie vocale</h2>
        <button
          onClick={onClose}
          className="p-2 text-white/70 hover:text-white rounded-full hover:bg-white/10 transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      {/* Zone de transcription */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        {/* Indicateur d'écoute */}
        <div className="mb-8">
          {isListening ? (
            <div className="relative">
              {/* Cercles d'animation */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 rounded-full bg-red-500/20 animate-ping" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-red-500/30 animate-pulse" />
              </div>
              <div className="relative w-20 h-20 rounded-full bg-red-500 flex items-center justify-center shadow-lg shadow-red-500/50">
                <Mic size={32} className="text-white" />
              </div>
            </div>
          ) : (
            <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center">
              <MicOff size={32} className="text-gray-400" />
            </div>
          )}
        </div>

        {/* Instructions */}
        <p className="text-white/80 text-center mb-4 text-sm">
          {isListening
            ? 'Parlez maintenant...'
            : hasTranscript
              ? 'Enregistrement terminé'
              : 'Appuyez sur le micro pour commencer'}
        </p>

        {/* Transcription */}
        <div className="w-full max-w-md min-h-[120px] bg-white/10 rounded-2xl p-4 backdrop-blur">
          {(transcript || interimTranscript) ? (
            <p className="text-white text-lg leading-relaxed">
              {transcript}
              {interimTranscript && (
                <span className="text-white/50"> {interimTranscript}</span>
              )}
            </p>
          ) : (
            <p className="text-white/40 text-center italic">
              {error || 'Votre transcription apparaîtra ici...'}
            </p>
          )}
        </div>

        {/* Message d'erreur */}
        {error && !isListening && (
          <div className="mt-4 px-4 py-2 bg-red-500/20 rounded-lg">
            <p className="text-red-300 text-sm text-center">{error}</p>
          </div>
        )}

        {/* Exemples */}
        {!hasTranscript && !isListening && (
          <div className="mt-6 text-center">
            <p className="text-white/50 text-xs mb-2">Exemples :</p>
            <div className="flex flex-wrap justify-center gap-2">
              <span className="px-3 py-1 bg-white/10 rounded-full text-white/70 text-xs">
                "5 kilos de tomates"
              </span>
              <span className="px-3 py-1 bg-white/10 rounded-full text-white/70 text-xs">
                "3 litres de lait"
              </span>
              <span className="px-3 py-1 bg-white/10 rounded-full text-white/70 text-xs">
                "10 unités de pain"
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-6 safe-area-bottom">
        {isListening ? (
          /* Pendant l'écoute : bouton stop */
          <button
            onClick={onToggleListening}
            className="w-full py-4 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-2xl flex items-center justify-center gap-2 transition-colors active:scale-[0.98]"
          >
            <MicOff size={20} />
            Arrêter l'enregistrement
          </button>
        ) : hasTranscript ? (
          /* Après l'écoute avec transcription : Valider / Annuler */
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={isProcessing}
              className="flex-1 py-4 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-2xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 active:scale-[0.98]"
            >
              <X size={20} />
              Annuler
            </button>
            <button
              onClick={onValidate}
              disabled={isProcessing}
              className="flex-1 py-4 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-2xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 active:scale-[0.98]"
            >
              {isProcessing ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Check size={20} />
              )}
              {isProcessing ? 'Traitement...' : 'Valider'}
            </button>
          </div>
        ) : (
          /* État initial : bouton démarrer */
          <button
            onClick={onToggleListening}
            className="w-full py-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-2xl flex items-center justify-center gap-2 transition-colors active:scale-[0.98]"
          >
            <Mic size={20} />
            Commencer l'enregistrement
          </button>
        )}

        {/* Indication secondaire */}
        {!isListening && hasTranscript && (
          <button
            onClick={onToggleListening}
            disabled={isProcessing}
            className="w-full mt-3 py-3 text-white/60 hover:text-white text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            <Mic size={16} />
            Réenregistrer
          </button>
        )}
      </div>
    </div>
  );
};
